'use client'

import { useState, useMemo } from 'react'
import Link from 'next/link'
import { iconFor, type Post } from './posts'

/**
 * Client-side category filter for the blog index. The page itself
 * stays a server component (so SEO metadata is preserved) — only
 * the interactive chip bar + grid live here.
 */
export default function BlogFilter({
  posts,
  categories,
}: {
  posts: Post[]
  categories: string[]
}) {
  const [active, setActive] = useState('All')

  const visible = useMemo(
    () => (active === 'All' ? posts : posts.filter(p => p.category === active)),
    [active, posts],
  )

  // Only show categories that actually have at least one post, plus 'All'.
  const usable = useMemo(() => {
    const present = new Set(posts.map(p => p.category))
    return categories.filter(c => c === 'All' || present.has(c))
  }, [categories, posts])

  return (
    <>
      {/* Filter chips */}
      <div style={{ borderBottom: '1px solid var(--border)', overflowX: 'auto', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '12px 24px', display: 'flex', gap: '8px' }}>
          {usable.map(cat => {
            const isActive = cat === active
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActive(cat)}
                aria-pressed={isActive}
                style={{
                  padding: '6px 16px', borderRadius: '100px', fontSize: '13px', fontWeight: 600,
                  background: isActive ? 'var(--accent)' : 'var(--bg-card)',
                  color: isActive ? '#fff' : 'var(--text-muted)',
                  border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
                }}
              >
                {cat}
              </button>
            )
          })}
        </div>
      </div>

      {/* Grid */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '48px 24px' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '8px' }}>
          {active === 'All' ? 'All Articles' : active}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '28px' }}>
          {visible.length} {visible.length === 1 ? 'article' : 'articles'}
        </p>

        {visible.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '15px', padding: '40px 0' }}>
            No articles in this category yet — check back soon.
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))', gap: '20px' }}>
            {visible.map(post => {
              const Icon = iconFor(post.icon)
              return (
              <Link key={post.slug} href={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                <div className="blog-card" style={{ height: '100%' }}>
                  <div style={{ height: '160px', background: 'linear-gradient(135deg, rgba(201,106,21,0.12), rgba(139,37,0,0.18))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={56} color="var(--accent)" strokeWidth={1.5} />
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent)', background: 'rgba(201,106,21,0.1)', padding: '2px 8px', borderRadius: '100px' }}>{post.category}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{post.readTime}</span>
                    </div>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', lineHeight: 1.4 }}>{post.title}</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '16px' }}>{post.excerpt}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                      <span>{post.date}</span>
                      <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Read →</span>
                    </div>
                  </div>
                </div>
              </Link>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
